import type { FC } from "react";

import {
  SONNER_ACTION_CLASS,
  SONNER_CONTENT_CLASS,
  SONNER_DEFAULTS,
  SONNER_DESCRIPTION_CLASS,
  SONNER_ICON_CLASS,
  SONNER_ICON_LOADING_CLASS,
  SONNER_LOADING_ICON,
  SONNER_ROOT_CLASS,
  SONNER_ROOT_FOCUS_CLASS,
  type TSonnerProps,
} from "@/ui/components/feedback/Sonner/Sonner.config";
import { SonnerClose } from "@/ui/components/feedback/Sonner/SonnerClose";
import { Button } from "@/ui/components/primitives/Button";
import { Icon } from "@/ui/components/primitives/Icon";
import { Label } from "@/ui/components/form/Label";
import { TextBody } from "@/ui/components/typography/TextBody";
import { cn } from "@/ui/lib/utils";

export const Sonner: FC<TSonnerProps> = (
  {
    title,
    description,
    detail,
    icon = SONNER_DEFAULTS.icon,
    loading = SONNER_DEFAULTS.loading,
    action,
    onDismiss,
    dismissLabel = SONNER_DEFAULTS.dismissLabel,
    className,
    role = SONNER_DEFAULTS.role,
    tabIndex = SONNER_DEFAULTS.tabIndex,
    "aria-live": ariaLive = "polite",
    ...props
  },
) => {
  const iconKey = loading ? SONNER_LOADING_ICON : icon;

  return (
    <div
      {...props}
      data-component="Sonner"
      role={role}
      tabIndex={tabIndex}
      aria-live={ariaLive}
      aria-atomic="true"
      aria-busy={loading || undefined}
      className={cn(SONNER_ROOT_CLASS, SONNER_ROOT_FOCUS_CLASS, className)}
    >
      { iconKey ? (
        <Icon
          icon={iconKey}
          aria-hidden="true"
          className={cn(SONNER_ICON_CLASS, loading && SONNER_ICON_LOADING_CLASS)}
        />
      ) : null }

      <div className={SONNER_CONTENT_CLASS}>
        <Label>{ title }</Label>
        { description ? (
          <TextBody size={2} color="inherit" isResponsive={false} className={SONNER_DESCRIPTION_CLASS}>{ description }</TextBody>
        ) : null }
        { detail ? (
          <TextBody size={3} color="secondary" isResponsive={false}>{ detail }</TextBody>
        ) : null }
      </div>

      { action ? (
        <Button variant="tertiary" size="sm" className={SONNER_ACTION_CLASS} onClick={action.onClick}>{ action.label }</Button>
      ) : null }

      { onDismiss ? (
        <SonnerClose aria-label={dismissLabel} onClick={onDismiss} />
      ) : null }
    </div>
  );
};

Sonner.displayName = "Sonner";
